import moment from 'moment-timezone'
import { parsePepHealthCheckLastResult } from '../parsePepHealthCheckLastResult'
import { formatHotelTime } from './formatHotelTime'
import {
  formatHeartbeatOperationalMessage,
  isHeartbeatTelemetryEvent,
  parseHeartbeatTaggedFields
} from './heartbeatOperationalMessage'
import {
  hasSuccessfulDataPullSince
} from './electronTelemetryTimelineHelpers'
import {
  isPepPermissionDeniedEvent,
  isSessionAuthPepDataPullFailure
} from './pepElectronOpsOperationalMessage'
import { isPepLoginUiTelemetryEvent } from './pepLoginUiOperationalMessage'

function readBool(value) {
  if (value === true || value === 'true') {
    return true
  }
  if (value === false || value === 'false') {
    return false
  }
  return null
}

function latestHeartbeatEvent(timelineEvents) {
  let latest = null
  for (const ev of timelineEvents || []) {
    if (!isHeartbeatTelemetryEvent(ev)) {
      continue
    }
    const t = moment(ev.occurredAt ?? ev.OccurredAt)
    if (!t.isValid()) {
      continue
    }
    if (!latest || t.isAfter(moment(latest.occurredAt ?? latest.OccurredAt))) {
      latest = ev
    }
  }
  return latest
}

function findLatestEventMatching(timelineEvents, predicate) {
  let latest = null
  for (const ev of timelineEvents || []) {
    if (!predicate(ev)) {
      continue
    }
    const t = moment(ev.occurredAt ?? ev.OccurredAt)
    if (!t.isValid()) {
      continue
    }
    if (!latest || t.isAfter(moment(latest.occurredAt ?? latest.OccurredAt))) {
      latest = ev
    }
  }
  return latest
}

function primaryDevice(devices) {
  if (!Array.isArray(devices) || devices.length === 0) {
    return null
  }
  return [...devices].sort((a, b) => {
    const ta = moment(a.lastSeenAt ?? a.LastSeenAt)
    const tb = moment(b.lastSeenAt ?? b.LastSeenAt)
    return tb.valueOf() - ta.valueOf()
  })[0]
}

function countOperationalEventTypes(timelineEvents) {
  let operational = 0
  let heartbeats = 0
  for (const ev of timelineEvents || []) {
    if (isHeartbeatTelemetryEvent(ev)) {
      heartbeats += 1
      continue
    }
    operational += 1
  }
  return { operational, heartbeats }
}

/**
 * Builds a support-facing "current situation" summary for the telemetry modal.
 */
export function deriveHotelTelemetrySituation({
  devices,
  timelineEvents,
  pepHealthCheckDisplay,
  lastResult,
  hotelTimeZoneId = null,
  now = moment.utc()
}) {
  const device = primaryDevice(devices)
  const latestHeartbeat = latestHeartbeatEvent(timelineEvents)
  const heartbeatFields = parseHeartbeatTaggedFields(
    latestHeartbeat?.message ?? latestHeartbeat?.Message ?? ''
  )
  const lastSeen = moment.utc(device?.lastSeenAt ?? device?.LastSeenAt)
  const hoursSinceSeen = lastSeen.isValid() ? now.diff(lastSeen, 'hours', true) : null
  const pepLoginActive =
    readBool(device?.pepLoginActive ?? device?.PepLoginActive) ??
    readBool(heartbeatFields.pepLoginActive)
  const dashboardLoginActive =
    readBool(device?.dashboardLoginActive ?? device?.DashboardLoginActive) ??
    readBool(heartbeatFields.dashboardLoginActive)
  const { operational, heartbeats } = countOperationalEventTypes(timelineEvents)
  const latestDataPull = findLatestEventMatching(timelineEvents, ev => {
    const type = (ev.eventType ?? ev.EventType ?? '').toString().toLowerCase()
    const code = (ev.eventCode ?? ev.EventCode ?? '').toString()
    return type.includes('datapullsucceeded') || code === 'PEP_DATA_PULL_OK'
  })
  const latestKeepAliveFail = findLatestEventMatching(
    timelineEvents,
    ev => (ev.eventCode ?? ev.EventCode ?? '') === 'PEP_KEEP_ALIVE_FAILED'
  )
  const latestRecoveryManual = findLatestEventMatching(
    timelineEvents,
    ev => (ev.eventCode ?? ev.EventCode ?? '') === 'PEP_SESSION_RECOVERY_MANUAL_LOGIN'
  )
  const latestSessionAuthFail = findLatestEventMatching(
    timelineEvents,
    ev =>
      (ev.eventCode ?? ev.EventCode ?? '') === 'PEP_KEEP_ALIVE_FAILED' ||
      isSessionAuthPepDataPullFailure(ev)
  )
  const latestPermissionDenied = findLatestEventMatching(
    timelineEvents,
    isPepPermissionDeniedEvent
  )
  const recentPullOk = hasSuccessfulDataPullSince(timelineEvents, now.clone().subtract(48, 'hours'))
  const parsedHealth = parsePepHealthCheckLastResult(lastResult)

  const bullets = []
  let severity = 'info'
  let headline = 'Desktop telemetry is available for this hotel.'
  let recommendedAction =
    'Review the event log below for sign-in, data pull, and recovery activity.'

  if (!device) {
    headline = 'No PEP desktop device has reported telemetry for this hotel.'
    severity = 'warn'
    bullets.push('No registered desktop device row was returned by the API.')
    recommendedAction =
      'Confirm the hotel installed the PEP desktop app and that at least one user signed in to the dashboard on that PC.'
    return { headline, bullets, severity, recommendedAction, sparseTimelineNote: null }
  }

  bullets.push(
    `Latest desktop contact: ${
      lastSeen.isValid()
        ? formatHotelTime(lastSeen.toISOString(), hotelTimeZoneId, { includeZoneLabel: true })
        : 'unknown'
    }${hoursSinceSeen != null ? ` (${Math.round(hoursSinceSeen)}h ago)` : ''}.`
  )
  bullets.push(
    `App version ${device.appVersion ?? device.AppVersion ?? 'unknown'} on device ${(
      device.deviceInstallationId ??
      device.DeviceInstallationId ??
      'unknown'
    ).slice(0, 8)}…`
  )

  if (dashboardLoginActive === true) {
    bullets.push('Dashboard sign-in is active on the latest device heartbeat.')
  } else if (dashboardLoginActive === false) {
    bullets.push('Dashboard sign-in is not active on the latest device heartbeat.')
    severity = 'warn'
  }

  if (pepLoginActive === true) {
    bullets.push('Hilton PEP sign-in is marked active on the latest device heartbeat.')
  } else if (pepLoginActive === false) {
    bullets.push(
      'Hilton PEP sign-in is not verified on the latest device heartbeat even though the app is running.'
    )
    severity = 'warn'
    recommendedAction =
      'Ask the hotel to open the desktop app and complete Hilton PEP sign-in, or wait for automatic shared-session recovery.'
  }

  if (latestHeartbeat) {
    bullets.push(formatHeartbeatOperationalMessage(latestHeartbeat))
  }

  if (latestDataPull) {
    bullets.push(
      `Last successful PEP data pull in this load: ${formatHotelTime(
        latestDataPull.occurredAt ?? latestDataPull.OccurredAt,
        hotelTimeZoneId,
        { includeZoneLabel: true }
      )}.`
    )
  } else if (!recentPullOk) {
    bullets.push('No successful PEP data pull appears in the loaded telemetry window.')
    if (severity !== 'error') {
      severity = 'warn'
    }
  }

  if (latestKeepAliveFail) {
    bullets.push(
      `Latest keep-alive failure: ${formatHotelTime(
        latestKeepAliveFail.occurredAt ?? latestKeepAliveFail.OccurredAt,
        hotelTimeZoneId,
        { includeZoneLabel: true }
      )}.`
    )
    severity = 'warn'
  }

  if (latestRecoveryManual) {
    bullets.push(
      'Automatic recovery could not restore Hilton PEP sign-in; the app requested the manual login screen.'
    )
    severity = 'warn'
  }

  if (latestPermissionDenied) {
    bullets.push(
      `Hilton denied PEP reservation-search access (permission, not a wrong password). Latest: ${formatHotelTime(
        latestPermissionDenied.occurredAt ?? latestPermissionDenied.OccurredAt,
        hotelTimeZoneId,
        { includeZoneLabel: true }
      )}.`
    )
    severity = 'error'
  }

  if (latestPermissionDenied) {
    headline =
      'Hilton has not granted this account PEP reservation-search access for this hotel.'
    severity = 'error'
    recommendedAction =
      'This is a Hilton-side access grant, not a sign-in problem — re-login will not fix it. Ask Hilton to enable PEP reservation-search for this property, or sign in with a different authorized Hilton user.'
  } else if (latestSessionAuthFail && pepLoginActive !== true) {
    headline = 'The desktop app is running but Hilton PEP sign-in is failing.'
    severity = 'error'
    recommendedAction =
      'Have the hotel complete Hilton PEP sign-in in the desktop app. Shared sessions are attempted automatically before the login screen is shown.'
  } else if (pepHealthCheckDisplay?.badgeKey === 'PepAuthorizationError') {
    headline = 'Health Check reports a PEP authorization problem at this hotel.'
    severity = 'error'
  } else if (parsedHealth?.badgeKey === 'PepAuthorizationError') {
    headline = 'Health Check reports a PEP authorization problem at this hotel.'
    severity = 'error'
  } else if (hoursSinceSeen != null && hoursSinceSeen >= 24) {
    headline = 'The desktop app has not sent fresh telemetry in more than 24 hours.'
    severity = 'warn'
    recommendedAction =
      'Confirm the hotel PC is on, online, and the Directful desktop app is running.'
  } else if (pepLoginActive === false) {
    headline = 'The desktop app is online but Hilton PEP sign-in is not verified.'
  }

  let sparseTimelineNote = null
  if (operational === 0 && heartbeats > 0) {
    sparseTimelineNote =
      'The event log may show only the latest heartbeat because older heartbeats are collapsed and this app version may not yet emit detailed recovery or data-pull events. Use the current situation summary above and the device login flags.'
  } else if (operational === 0 && heartbeats === 0) {
    sparseTimelineNote =
      'No timeline events were returned for the selected range. Expand the Timeline dates or reload after the hotel desktop app sends more telemetry.'
  }

  const latestLoginUi = findLatestEventMatching(timelineEvents, isPepLoginUiTelemetryEvent)
  if (latestLoginUi) {
    bullets.push(
      `Latest PEP login UI activity: ${formatHotelTime(
        latestLoginUi.occurredAt ?? latestLoginUi.OccurredAt,
        hotelTimeZoneId,
        { includeZoneLabel: true }
      )}.`
    )
  }

  return {
    headline,
    bullets: [...new Set(bullets.filter(Boolean))],
    severity,
    recommendedAction,
    sparseTimelineNote
  }
}
